/**
 * Who hands a lead to the Agent (B8, diagram 03): the Messenger, and staff who
 * can also work the queue. The push intent checks it, and (tv-3zjo) only a write
 * by one of these roles — or the server itself — stamps the customer's durable
 * "pushed to the Agent" marker.
 */
export const AGENT_PUSH_ROLES: ReadonlySet<string> = new Set(['messenger', 'bookings-agent', 'georgina', 'owner', 'studio-manager']);

/** The Messenger's channels (Instagram and Facebook DMs). */
export const MESSENGER_DM_CHANNELS: ReadonlySet<string> = new Set(['ig-dm', 'meta-dm']);

/** The bits of a sequence row `isWithAgent` reads (stage may be 1|2|3|'red'). */
export interface WithAgentSequence {
  stage?: unknown;
  sourceConversationId?: string | null;
}

/**
 * enquiries-9: is this lead the Agent's (so NOT the Messenger's to answer, and
 * never a Messenger "Unanswered > 24h")? ONE rule for the Messenger inbox, the
 * Ops worklist and the server's opsView.
 *
 * Anas 8 Oct: "once they collect the number and push to agent, then agent owns
 * that lead, and the agent needs to message them back", and the lead stays the
 * Agent's for good: after a booking, and after a red sequence. So a lead is the
 * Agent's once it has been pushed, by either sign:
 *
 * - a sequence row carrying a sourceConversationId (pushed from a conversation),
 *   whatever its stage, red included;
 * - the durable `pushedToAgentAt` marker, which outlives the row (a booking,
 *   cancel or lapse deletes the row).
 *
 * A DM from the client after the push never hands the lead back; the Agent
 * sees it as a "Client messaged" alert (`clientMessagedAfterPush`) and replies.
 * A row the Agent queued on its own (no source conversation, never pushed) is
 * not a push.
 */
export function isWithAgent(
  customer: { pushedToAgentAt?: string | Date | null } | null | undefined,
  seq: WithAgentSequence | null | undefined,
): boolean {
  return !!seq?.sourceConversationId || !!customer?.pushedToAgentAt;
}

/** A conversation entry, as much of it as the DM rules read. */
export interface DmEntryLike {
  id?: string;
  channel?: string;
  direction?: string;
  authorRole?: string;
  body?: string;
  timestamp?: string;
  at?: string | Date;
}

const entryMs = (e: DmEntryLike): number => new Date((e.timestamp ?? e.at) as string).getTime();

/**
 * tv-9nae: the demo clock doesn't tick inside a day, so a thread's entries and
 * the push could all carry the same timestamp, and "after the push" / "a reply
 * after the DM" would be undecidable (stores order equal timestamps by id, not
 * by arrival). Entries added to a thread are therefore stamped strictly after
 * the thread's last entry: an entry at or a little before the last one (within
 * the hour, i.e. the same demo moment, not a deliberately back-dated or
 * real-clock entry) lands one second after it. The store leaves a
 * server-authored entry at the server's time.
 */
export const THREAD_STAMP_WINDOW_MS = 60 * 60 * 1000;
const THREAD_STAMP_STEP_MS = 1000;

export function nextThreadTimestamp(lastIso: string | undefined, iso: string): string {
  if (!lastIso) return iso;
  const last = Date.parse(lastIso);
  const t = Date.parse(iso);
  if (!Number.isFinite(last) || !Number.isFinite(t)) return iso;
  if (t > last || last - t >= THREAD_STAMP_WINDOW_MS) return iso;
  return new Date(last + THREAD_STAMP_STEP_MS).toISOString();
}

/**
 * tv-9nae / tv-hnjy.8: the push marker sits just after everything already in
 * the thread (the DM the lead was pushed from included), whatever the clock:
 * the later of now and the thread's last entry + 1ms. So every DM before the
 * push is earlier than the marker, and a DM after it is at or after it.
 */
export function pushMarkerIso(nowIso: string, lastEntryIso: string | undefined): string {
  const now = Date.parse(nowIso);
  const last = lastEntryIso ? Date.parse(lastEntryIso) : NaN;
  if (!Number.isFinite(last) || last < now) return new Date(now).toISOString();
  return new Date(last + 1).toISOString();
}

/** The latest timestamp in a thread (undefined for an empty one). */
export function threadLastTimestamp(conversation: ReadonlyArray<DmEntryLike> | null | undefined): string | undefined {
  let best: number | undefined;
  for (const e of conversation ?? []) {
    const t = entryMs(e);
    if (Number.isFinite(t) && (best === undefined || t > best)) best = t;
  }
  return best === undefined ? undefined : new Date(best).toISOString();
}

const isClientDm = (e: DmEntryLike): boolean => e.direction === 'in' && MESSENGER_DM_CHANNELS.has(e.channel ?? '');

/**
 * The client's latest Instagram/Facebook DM at or after the push marker, while
 * nobody (not counting system sends) has replied since. No ownership check: the
 * Ops escalation calls it for leads already known to be the Agent's. The marker
 * is stamped after everything in the thread at the push (`pushMarkerIso`), so
 * "at or after" never catches the DM the lead was pushed from. A reply counts
 * when it is later than the DM, or at the same time but later in the thread.
 */
export function unansweredDmAfterPush<E extends DmEntryLike>(
  customer: { pushedToAgentAt?: string | Date | null } | null | undefined,
  conversation: ReadonlyArray<E> | null | undefined,
): E | undefined {
  const pushedMs = new Date(customer?.pushedToAgentAt as string).getTime();
  if (!Number.isFinite(pushedMs)) return undefined;
  const list = conversation ?? [];
  let latest = -1;
  list.forEach((e, i) => {
    if (!isClientDm(e)) return;
    const t = entryMs(e);
    if (!Number.isFinite(t) || t < pushedMs) return;
    if (latest < 0 || t >= entryMs(list[latest]!)) latest = i;
  });
  if (latest < 0) return undefined;
  const at = entryMs(list[latest]!);
  const answered = list.some(
    (e, i) => e.direction === 'out' && e.authorRole !== 'system' && (entryMs(e) > at || (entryMs(e) === at && i > latest)),
  );
  return answered ? undefined : list[latest];
}

/**
 * Anas 8 Oct: the "Client messaged" alert. The client's latest unanswered
 * Instagram/Facebook DM after the push (`unansweredDmAfterPush`), on a lead
 * that is the Agent's (`isWithAgent`), red sequences included.
 */
export function clientMessagedAfterPush<E extends DmEntryLike>(
  customer: { pushedToAgentAt?: string | Date | null } | null | undefined,
  seq: WithAgentSequence | null | undefined,
  conversation: ReadonlyArray<E> | null | undefined,
): E | undefined {
  if (!isWithAgent(customer, seq)) return undefined;
  return unansweredDmAfterPush(customer, conversation);
}
